import type { EvaluationLine, UciMove } from '../api/types';

/**
 * Pure parsing of Stockfish UCI output into the §4 `EvaluationLine` shape.
 *
 * Kept free of Worker/DOM so the fiddly parts — mate scores, MultiPV ranks,
 * bound-qualified scores — are unit-tested without an engine. The emitted shape is
 * deliberately the *same* `EvaluationLine` the server returns, so
 * `state/eval.ts` (score formatting, win-fraction, best-line selection) is reused
 * unchanged for local results.
 */

/** A parsed `info` line that carried a usable score. */
export interface InfoLine {
  /** MultiPV rank, 1-based. Absent `multipv` means rank 1. */
  rank: number;
  score: EvaluationLine['score'];
  depth: number;
  nodes: number;
  timeMs: number;
  pv: UciMove[];
}

function token(parts: string[], name: string): string | null {
  const index = parts.indexOf(name);
  if (index === -1 || index + 1 >= parts.length) return null;
  return parts[index + 1];
}

function intToken(parts: string[], name: string, fallback: number): number {
  const raw = token(parts, name);
  if (raw === null) return fallback;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : fallback;
}

/**
 * Parse one `info …` line, or return `null` when it carries nothing displayable.
 *
 * Two classes of line are deliberately dropped:
 * - **`currmove` progress lines**, which have no score at all;
 * - **`lowerbound` / `upperbound` scores**, which are aspiration-window artifacts
 *   rather than real evaluations. Rendering them makes the eval bar jump to
 *   absurd values mid-search, so they are treated as "no news".
 */
export function parseInfoLine(line: string): InfoLine | null {
  if (!line.startsWith('info ')) return null;
  const parts = line.trim().split(/\s+/);

  const scoreIndex = parts.indexOf('score');
  if (scoreIndex === -1) return null;
  const scoreKind = parts[scoreIndex + 1];
  const scoreRaw = parts[scoreIndex + 2];
  if (scoreKind !== 'cp' && scoreKind !== 'mate') return null;
  const scoreValue = Number.parseInt(scoreRaw, 10);
  if (!Number.isFinite(scoreValue)) return null;

  const qualifier = parts[scoreIndex + 3];
  if (qualifier === 'lowerbound' || qualifier === 'upperbound') return null;

  const pvIndex = parts.indexOf('pv');
  const pv = pvIndex === -1 ? [] : parts.slice(pvIndex + 1).map((move) => move.toLowerCase());

  return {
    rank: Math.max(1, intToken(parts, 'multipv', 1)),
    score:
      scoreKind === 'cp'
        ? { type: 'cp', value: scoreValue }
        : { type: 'mate', value: scoreValue },
    depth: intToken(parts, 'depth', 0),
    nodes: intToken(parts, 'nodes', 0),
    timeMs: intToken(parts, 'time', 0),
    pv,
  };
}

/** Extract the move from a `bestmove e2e4 ponder e7e5` line, or `null`. */
export function parseBestMove(line: string): UciMove | null {
  if (!line.startsWith('bestmove')) return null;
  const move = line.trim().split(/\s+/)[1];
  if (!move || move === '(none)' || move === '0000') return null;
  return move.toLowerCase();
}

/** Convert a parsed info line into the §4 wire shape (`nodes` is a decimal string). */
export function toEvaluationLine(info: InfoLine): EvaluationLine {
  return {
    rank: info.rank,
    score: info.score,
    perspective: 'side_to_move',
    depth: info.depth,
    nodes: String(info.nodes),
    timeMs: info.timeMs,
    bestMove: info.pv[0] ?? null,
    pv: info.pv,
  };
}

/**
 * Fold a stream of info lines into the current best set of lines, ranked.
 *
 * Stockfish re-emits each MultiPV rank at every depth, so the newest line for a
 * rank replaces the previous one. Ranks are returned in ascending order, which is
 * the order the UI renders them.
 */
export function mergeLines(
  previous: readonly EvaluationLine[],
  info: InfoLine,
): EvaluationLine[] {
  const next = toEvaluationLine(info);
  const merged = previous.filter((line) => line.rank !== next.rank);
  merged.push(next);
  merged.sort((a, b) => a.rank - b.rank);
  return merged;
}

/** The `setoption` commands sent once per engine session. */
export function optionCommands(options: {
  hashMb: number;
  multiPv: number;
}): string[] {
  return [
    // Single-threaded build: asking for more threads is silently ignored at best.
    'setoption name Threads value 1',
    `setoption name Hash value ${options.hashMb}`,
    `setoption name MultiPV value ${options.multiPv}`,
    'setoption name Ponder value false',
  ];
}
